import Cluster from 'frontend/clusters/cluster.vue'
// eslint-disable-next-line import/default
import TableFileThumbnails from 'frontend/common/files/components/table-file-thumbnails.vue'
import { useActiveStorageBlobUrl } from 'frontend/common/files/composables/useActiveStorageBlobUrl'
import TrueFalseLabel from 'frontend/common/true-false-label.vue'
import { IDataModelTypeConfig } from 'frontend/dataModels/types/IDataModelTypeConfig'
import Location from 'frontend/locations/location.vue'
import TPEnabledStatuses from 'frontend/transportation-points/transportation-point-enabled-statuses.vue'
import { markRaw } from 'vue'

const { mapResourceImageToFileResponseEntity } = useActiveStorageBlobUrl()

export const TransportationPointDataModelTypeConfig: IDataModelTypeConfig = {
  fields: [
    'id',
    'eid',
    'priority',
    'address',
    'name',
    'description',
    'is_load_zone',
    'is_specially_abled_load_zone',
    'updated_at',
    'enabled_for_arrival',
    'enabled_for_departure',
    'enabled_for_onsite',
    'enabled_for_shuttle',
    'latitude',
    'longitude',
    { location: ['id', 'name', 'time_zone', { cluster: ['id', 'name', 'time_zone'] }] },
    { transportationPointImages: ['id', 'is_primary', 'urls'] },
  ],
  subscriptions: {
    items: 'TransportationPointsChannel',
    locations: 'LocationsSimpleChannel',
    clusters: 'ClustersSimpleChannel',
  },
  defaultColumns: [
    'priority',
    'eid',
    'name',
    'location',
    'address',
    'enabled',
    'isLoadZone',
    'isSpeciallyAbledLoadZone',
    'updatedAt',
  ],
  columnConfig: {
    priority: {
      label: 'Priority',
      header: {
        sort: 'priority',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'simple',
        path: 'priority',
      },
    },
    eid: {
      label: 'EID',
      header: {
        sort: 'eid',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'simple',
        path: 'eid',
      },
    },
    name: {
      label: 'Name',
      header: {
        sort: 'lowerName',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'simple',
        path: 'name',
      },
    },
    location: {
      label: 'Location',
      header: {
        sort: 'locationName',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: Location,
        componentProps: el => ({ location: el.location, nameNowrap: true }),
      },
    },
    cluster: {
      label: 'Cluster',
      header: {
        sort: 'locationClusterName',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: Cluster,
        componentProps: el => ({ cluster: el.location?.cluster }),
      },
    },
    address: {
      label: 'Address',
      header: {
        sort: 'lowerAddress',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'simple',
        path: 'address',
      },
    },
    enabled: {
      label: 'Enabled',
      header: {},
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: TPEnabledStatuses,
        componentProps: tp => ({ transportationPoint: tp }),
      },
    },
    isLoadZone: {
      label: 'Load Zone',
      header: {
        sort: 'isLoadZone',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: markRaw(TrueFalseLabel),
        componentProps: tp => ({ item: tp.isLoadZone }),
      },
    },
    isSpeciallyAbledLoadZone: {
      label: 'Specially Abled Load Zone',
      header: {
        sort: 'isSpeciallyAbledLoadZone',
      },
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: markRaw(TrueFalseLabel),
        componentProps: tp => ({ item: tp.isSpeciallyAbledLoadZone }),
      },
    },
    updatedAt: {
      label: 'Updated At',
      header: {},
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.updatedAt,
        }),
      },
    },
    description: {
      label: 'Description',
      header: {
        sort: 'description',
      },
      cell: {
        kind: 'component',
        component: 'ellipsis-with-tooltip',
        componentProps: item => {
          return {
            text: item.description,
            limit: 250,
          }
        },
      }
    },
    updatedDate: {
      label: 'Updated Date',
      header: {},
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.updatedAt,
          format: 'dateOnly',
        }),
      },
    },
    updatedTime: {
      label: 'Updated Time',
      header: {},
      cell: {
        class: 'text-nowrap',
        kind: 'component',
        component: 'formatted-time',
        componentProps: item => ({
          t: item.updatedAt,
          format: 'timeOnly',
        }),
      },
    },
    images: {
      label: 'Images',
      cell: {
        kind: 'component',
        component: TableFileThumbnails,
        componentProps: transportationPoint => ({
          files: transportationPoint.transportationPointImages.map(transportationPointImage => {
            return mapResourceImageToFileResponseEntity(transportationPointImage)
          }),
          eid: transportationPoint.eid,
        }),
      },
    },
    latitude: {
      label: 'Latitude',
      header: {
        sort: 'latitude',
      },
      cell: {
        kind: 'function',
        fn(item) {
          return item.latitude ? Number(item.latitude).toFixed(6) : ''
        },
      },
    },
    longitude: {
      label: 'Longitude',
      header: {
        sort: 'longitude',
      },
      cell: {
        kind: 'function',
        fn(item) {
          return item.longitude ? Number(item.longitude).toFixed(6) : ''
        },
      },
    },
  },
}
